import axiosInstance from "../components/api/axiosInstance";

/**
 * Parse id from POST /create-project — nested shapes (data, project, result).
 */
export function extractCreatedProjectId(created, depth = 0) {
  if (created == null || depth > 4) return null;
  const obj = Array.isArray(created) ? created[0] : created;
  if (!obj || typeof obj !== "object") return null;

  const raw =
    obj.Project_Id ??
    obj.project_id ??
    obj.ProjectId ??
    obj.projectId ??
    obj.insertId ??
    obj.last_insert_id ??
    obj.ID ??
    obj.Id;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;

  const inner = obj.data ?? obj.project ?? obj.result ?? obj.payload;
  if (inner != null && inner !== obj) {
    const nested = extractCreatedProjectId(inner, depth + 1);
    if (nested != null) return nested;
  }

  const lastResort = Number(obj.id);
  if (Number.isFinite(lastResort) && lastResort > 0) return lastResort;

  return null;
}

/**
 * Parse id from POST /sub-projects (create) — nested shapes.
 */
export function extractCreatedSubProjectId(created, depth = 0) {
  if (created == null || depth > 4) return null;
  const obj = Array.isArray(created) ? created[0] : created;
  if (!obj || typeof obj !== "object") return null;

  const raw =
    obj.Sub_Project_Id ??
    obj.sub_project_id ??
    obj.SubProjectId ??
    obj.subProjectId ??
    obj.insertId ??
    obj.last_insert_id ??
    obj.ID ??
    obj.Id;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;

  const inner = obj.data ?? obj.sub_project ?? obj.subProject ?? obj.result ?? obj.payload;
  if (inner != null && inner !== obj) {
    const nested = extractCreatedSubProjectId(inner, depth + 1);
    if (nested != null) return nested;
  }

  const lastResort = Number(obj.id);
  if (Number.isFinite(lastResort) && lastResort > 0) return lastResort;

  return null;
}

/** Normalize file row from POST /file or GET /files?project_id= / ?sub_project_id= */
export function normalizeProjectFileRecord(raw) {
  if (!raw || typeof raw !== "object") return null;
  const rec = {
    fileId: raw.File_Id ?? raw.file_id,
    fileName: raw.File_Name ?? raw.file_name ?? "Attachment",
    filePath: raw.File_Path ?? raw.file_path ?? "",
    fileUrl: raw.File_URL ?? raw.file_url ?? "",
    projectId: raw.Project_Id ?? raw.project_id,
    subProjectId: raw.Sub_Project_Id ?? raw.sub_project_id,
    issueId: raw.Issue_Id ?? raw.issue_id,
    uploadedOn: raw.Uploaded_On ?? raw.uploaded_on,
    uploadedBy: raw.Uploaded_By ?? raw.uploaded_by
  };
  if (rec.fileId == null && !rec.fileUrl) return null;
  return rec;
}

export function extractProjectFilesFromPayload(project) {
  if (!project) return [];
  const raw =
    project.Files ??
    project.files ??
    project.File_List ??
    project.file_list ??
    project.Attachments ??
    project.attachments ??
    project.Project_Files ??
    project.project_files ??
    [];
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeProjectFileRecord).filter(Boolean);
}

/**
 * POST multipart: project_id, file — same /file endpoint as issues
 */
export async function uploadProjectFile(projectId, file) {
  const id = Number(projectId);
  if (!Number.isFinite(id)) {
    throw new Error("Invalid project id");
  }
  if (!file || !(file instanceof Blob)) {
    throw new Error("No file selected");
  }
  const formData = new FormData();
  formData.append("project_id", String(id));
  formData.append("file", file);
  const response = await axiosInstance.post("/file", formData);
  const data = response.data?.data ?? response.data;
  return normalizeProjectFileRecord(data) || data;
}

/**
 * DELETE /delete-file/:file_id — same endpoint as issues.
 */
export async function deleteProjectFile(fileId) {
  const id = Number(fileId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Invalid file id");
  }
  const customPath = process.env.REACT_APP_PROJECT_FILE_DELETE_PATH || process.env.REACT_APP_ISSUE_FILE_DELETE_PATH;
  const path =
    customPath && String(customPath).trim()
      ? String(customPath).replace(/\{id\}/g, String(id)).replace(/:id/g, String(id))
      : `/delete-file/${id}`;

  try {
    const response = await axiosInstance.delete(path);
    return response.data?.data ?? response.data;
  } catch (err) {
    if (err?.response?.status === 405) {
      const response = await axiosInstance.post(path);
      return response.data?.data ?? response.data;
    }
    throw err;
  }
}

const validateStatusProbe = (status) =>
  (status >= 200 && status < 300) || status === 404 || status === 405;

function parseFilesArrayResponse(res) {
  const body = res?.data;
  const arr = Array.isArray(body)
    ? body
    : Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body?.files)
        ? body.files
        : [];
  return (arr || []).map(normalizeProjectFileRecord).filter(Boolean);
}

/**
 * GET /files?project_id=
 */
export async function fetchProjectFiles(projectId) {
  const id = Number(projectId);
  if (!Number.isFinite(id)) return [];

  const customPath = process.env.REACT_APP_PROJECT_FILES_LIST_PATH;
  const urls = [];
  if (customPath && String(customPath).trim()) {
    urls.push({ url: String(customPath).trim(), params: { project_id: id } });
  }
  urls.push(
    { url: "/files", params: { project_id: id } },
    { url: "/files", params: { Project_Id: id } }
  );

  for (const { url, params } of urls) {
    const res = await axiosInstance.get(url, {
      params,
      validateStatus: validateStatusProbe
    });
    if (res.status === 404 || res.status === 405) continue;
    if (res.status >= 200 && res.status < 300) {
      return parseFilesArrayResponse(res);
    }
  }
  return [];
}

export function extractSubProjectFilesFromPayload(subproject) {
  if (!subproject) return [];
  const raw =
    subproject.Files ??
    subproject.files ??
    subproject.File_List ??
    subproject.file_list ??
    subproject.Attachments ??
    subproject.attachments ??
    subproject.Sub_Project_Files ??
    subproject.sub_project_files ??
    [];
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeProjectFileRecord).filter(Boolean);
}

/**
 * POST multipart: sub_project_id, file — same /file endpoint
 */
export async function uploadSubProjectFile(subProjectId, file) {
  const id = Number(subProjectId);
  if (!Number.isFinite(id)) {
    throw new Error("Invalid subproject id");
  }
  if (!file || !(file instanceof Blob)) {
    throw new Error("No file selected");
  }
  const formData = new FormData();
  formData.append("sub_project_id", String(id));
  formData.append("file", file);
  const response = await axiosInstance.post("/file", formData);
  const data = response.data?.data ?? response.data;
  return normalizeProjectFileRecord(data) || data;
}

/**
 * GET /files?sub_project_id=
 */
export async function fetchSubProjectFiles(subProjectId) {
  const id = Number(subProjectId);
  if (!Number.isFinite(id)) return [];

  const customPath = process.env.REACT_APP_SUBPROJECT_FILES_LIST_PATH;
  const urls = [];
  if (customPath && String(customPath).trim()) {
    urls.push({ url: String(customPath).trim(), params: { sub_project_id: id } });
  }
  urls.push(
    { url: "/files", params: { sub_project_id: id } },
    { url: "/files", params: { Sub_Project_Id: id } }
  );

  for (const { url, params } of urls) {
    const res = await axiosInstance.get(url, {
      params,
      validateStatus: validateStatusProbe
    });
    if (res.status === 404 || res.status === 405) continue;
    if (res.status >= 200 && res.status < 300) {
      return parseFilesArrayResponse(res);
    }
  }
  return [];
}

/** Same as deleteProjectFile — DELETE /delete-file/:id */
export const deleteSubProjectFile = deleteProjectFile;
